import { useMemo, useEffect } from 'react';
import { Twitter, Facebook, Linkedin, Link2 } from 'lucide-react';
import { toast } from 'sonner';

// Helper function to extract font family name from CSS fontFamily string
const extractFontName = (fontFamily) => {
  if (!fontFamily) return null;
  // Remove quotes and fallback fonts like ", sans-serif"
  const match = fontFamily.match(/^['"]?([^'"`,]+)/);
  return match ? match[1].trim() : null;
};

// Helper function to load Google Font dynamically
const loadGoogleFont = (fontFamily, weight = 400) => {
  const fontName = extractFontName(fontFamily);
  if (!fontName) return;
  
  // Check if font is already loaded
  const linkId = `google-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(linkId)) return;
  
  // Create and append the link element
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${weight}&display=swap`;
  document.head.appendChild(link);
};

// Border style configurations - Gothic theme ornaments
const borderStyleConfigs = {
  none: { label: 'No Border', base: '', ringBase: '' },
  cathedral: { 
    label: 'Cathedral Arch', 
    base: 'border-4',
    ringBase: 'ring-2 ring-offset-6 ring-offset-[#160A0D]',
    corners: true,
    cornerStyle: 'diamond'
  },
  illuminated: { 
    label: 'Illuminated Manuscript', 
    base: 'border-[6px]',
    ringBase: 'ring-2 ring-offset-8 ring-offset-[#160A0D]',
    corners: true,
    cornerStyle: 'square'
  },
  ironcross: { 
    label: 'Iron Cross', 
    base: 'border-[5px]',
    ringBase: 'ring-2 ring-offset-4 ring-offset-[#160A0D]',
    corners: true,
    cornerStyle: 'cross'
  },
  gargoyle: { 
    label: 'Gargoyle Frame', 
    base: 'border-8',
    ringBase: 'ring-3 ring-offset-6 ring-offset-[#160A0D]',
    corners: true,
    cornerStyle: 'triangle'
  },
  rosewindow: { 
    label: 'Rose Window', 
    base: 'border-4',
    ringBase: 'ring-2 ring-offset-5 ring-offset-[#160A0D] rounded-xl',
    corners: true,
    cornerStyle: 'circle'
  },
  cryptvines: { 
    label: 'Crypt Vines', 
    base: 'border-[3px]',
    ringBase: 'ring-1 ring-offset-3 ring-offset-[#160A0D]',
    corners: true,
    cornerStyle: 'dot'
  },
  sacredscript: { 
    label: 'Sacred Script', 
    base: 'border-4',
    ringBase: 'ring-2 ring-offset-4 ring-offset-[#160A0D]',
    corners: true,
    cornerStyle: 'star'
  }
};

export const FontPreview = ({ font, previewText, borderStyle = 'none', borderColor = '#0A0A0A', className = '', showShareButtons = false }) => {
  // Load Google Font dynamically when font changes
  useEffect(() => {
    if (font?.css_properties?.fontFamily) {
      const weight = font.style_properties?.weight || 400;
      loadGoogleFont(font.css_properties.fontFamily, weight);
    }
  }, [font?.css_properties?.fontFamily, font?.style_properties?.weight]);

  const styleObject = useMemo(() => {
    if (!font) return {};
    
    const style = {};
    
    // Apply CSS properties from the font
    if (font.css_properties) {
      Object.entries(font.css_properties).forEach(([key, value]) => {
        // Convert kebab-case to camelCase
        const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        style[camelKey] = value;
      });
    }
    
    // Apply style properties
    if (font.style_properties) {
      const props = font.style_properties;
      if (props.weight) style.fontWeight = props.weight;
      if (props.style) style.fontStyle = props.style;
      if (props.letterSpacing) style.letterSpacing = props.letterSpacing;
      if (props.lineHeight) style.lineHeight = props.lineHeight;
      if (props.textTransform) style.textTransform = props.textTransform;
      if (props.stretch) style.fontStretch = props.stretch;
    }
    
    return style;
  }, [font]);

  const displayText = previewText || font?.preview_text || 'For God so loved the world. — John 3:16';
  const borderConfig = borderStyleConfigs[borderStyle] || borderStyleConfigs.none;

  // Share URL includes border style and color parameters for the preview page
  const shareUrl = font ? `${window.location.origin}/font/${font.id}/preview?border=${borderStyle}&borderColor=${encodeURIComponent(borderColor)}` : '';
  const shareText = font ? `Check out "${font.name}" - an AI-generated custom font by FuntzGuntzAI!` : '';

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    
    let url;
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'pinterest':
        url = `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        return;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className={`font-preview-wrapper ${className}`} data-testid="font-preview-wrapper">
      {/* Square Preview Container */}
      <div 
        className={`
          aspect-square w-full max-w-[500px] mx-auto
          bg-[#F5EFE3] flex items-center justify-center p-10
          relative overflow-hidden
          ${borderConfig.base} ${borderConfig.ringBase}
        `}
        style={{
          borderColor: borderColor,
          '--tw-ring-color': borderColor
        }}
        data-testid="font-preview"
      >
        {/* Corner decorations based on Gothic style */}
        {borderConfig.corners && borderConfig.cornerStyle === 'diamond' && (
          <>
            <div className="absolute top-3 left-3 w-5 h-5 rotate-45 opacity-90" style={{ backgroundColor: borderColor }} />
            <div className="absolute top-3 right-3 w-5 h-5 rotate-45 opacity-90" style={{ backgroundColor: borderColor }} />
            <div className="absolute bottom-3 left-3 w-5 h-5 rotate-45 opacity-90" style={{ backgroundColor: borderColor }} />
            <div className="absolute bottom-3 right-3 w-5 h-5 rotate-45 opacity-90" style={{ backgroundColor: borderColor }} />
          </>
        )}
        {borderConfig.corners && borderConfig.cornerStyle === 'square' && (
          <>
            <div className="absolute top-3 left-3 w-6 h-6 opacity-90 border-2" style={{ backgroundColor: borderColor, borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full border" style={{ borderColor: '#F5EFE3' }} />
            </div>
            <div className="absolute top-3 right-3 w-6 h-6 opacity-90 border-2" style={{ backgroundColor: borderColor, borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full border" style={{ borderColor: '#F5EFE3' }} />
            </div>
            <div className="absolute bottom-3 left-3 w-6 h-6 opacity-90 border-2" style={{ backgroundColor: borderColor, borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full border" style={{ borderColor: '#F5EFE3' }} />
            </div>
            <div className="absolute bottom-3 right-3 w-6 h-6 opacity-90 border-2" style={{ backgroundColor: borderColor, borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full border" style={{ borderColor: '#F5EFE3' }} />
            </div>
          </>
        )}
        {borderConfig.corners && borderConfig.cornerStyle === 'cross' && (
          <>
            <div className="absolute top-4 left-4 flex items-center justify-center">
              <div className="w-1.5 h-5 opacity-90" style={{ backgroundColor: borderColor }} />
              <div className="absolute w-5 h-1.5 opacity-90" style={{ backgroundColor: borderColor }} />
            </div>
            <div className="absolute top-4 right-4 flex items-center justify-center">
              <div className="w-1.5 h-5 opacity-90" style={{ backgroundColor: borderColor }} />
              <div className="absolute w-5 h-1.5 opacity-90" style={{ backgroundColor: borderColor }} />
            </div>
            <div className="absolute bottom-4 left-4 flex items-center justify-center">
              <div className="w-1.5 h-5 opacity-90" style={{ backgroundColor: borderColor }} />
              <div className="absolute w-5 h-1.5 opacity-90" style={{ backgroundColor: borderColor }} />
            </div>
            <div className="absolute bottom-4 right-4 flex items-center justify-center">
              <div className="w-1.5 h-5 opacity-90" style={{ backgroundColor: borderColor }} />
              <div className="absolute w-5 h-1.5 opacity-90" style={{ backgroundColor: borderColor }} />
            </div>
          </>
        )}
        {borderConfig.corners && borderConfig.cornerStyle === 'triangle' && (
          <>
            <div className="absolute top-0 left-0 w-0 h-0 border-l-[20px] border-t-[20px] border-l-transparent" style={{ borderTopColor: borderColor }} />
            <div className="absolute top-0 right-0 w-0 h-0 border-r-[20px] border-t-[20px] border-r-transparent" style={{ borderTopColor: borderColor }} />
            <div className="absolute bottom-0 left-0 w-0 h-0 border-l-[20px] border-b-[20px] border-l-transparent" style={{ borderBottomColor: borderColor }} />
            <div className="absolute bottom-0 right-0 w-0 h-0 border-r-[20px] border-b-[20px] border-r-transparent" style={{ borderBottomColor: borderColor }} />
          </>
        )}
        {borderConfig.corners && borderConfig.cornerStyle === 'circle' && (
          <>
            <div className="absolute top-3 left-3 w-6 h-6 rounded-full border-2 opacity-90" style={{ borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full" style={{ backgroundColor: borderColor }} />
            </div>
            <div className="absolute top-3 right-3 w-6 h-6 rounded-full border-2 opacity-90" style={{ borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full" style={{ backgroundColor: borderColor }} />
            </div>
            <div className="absolute bottom-3 left-3 w-6 h-6 rounded-full border-2 opacity-90" style={{ borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full" style={{ backgroundColor: borderColor }} />
            </div>
            <div className="absolute bottom-3 right-3 w-6 h-6 rounded-full border-2 opacity-90" style={{ borderColor: borderColor }}>
              <div className="absolute inset-1 rounded-full" style={{ backgroundColor: borderColor }} />
            </div>
          </>
        )}
        {borderConfig.corners && borderConfig.cornerStyle === 'dot' && (
          <>
            <div className="absolute top-3 left-3 w-3 h-3 rounded-full opacity-80" style={{ backgroundColor: borderColor }} />
            <div className="absolute top-3 right-3 w-3 h-3 rounded-full opacity-80" style={{ backgroundColor: borderColor }} />
            <div className="absolute bottom-3 left-3 w-3 h-3 rounded-full opacity-80" style={{ backgroundColor: borderColor }} />
            <div className="absolute bottom-3 right-3 w-3 h-3 rounded-full opacity-80" style={{ backgroundColor: borderColor }} />
          </>
        )}
        {borderConfig.corners && borderConfig.cornerStyle === 'star' && (
          <>
            <div className="absolute top-2 left-2 text-lg opacity-90" style={{ color: borderColor }}>✦</div>
            <div className="absolute top-2 right-2 text-lg opacity-90" style={{ color: borderColor }}>✦</div>
            <div className="absolute bottom-2 left-2 text-lg opacity-90" style={{ color: borderColor }}>✦</div>
            <div className="absolute bottom-2 right-2 text-lg opacity-90" style={{ color: borderColor }}>✦</div>
          </>
        )}
        
        {/* Font Preview Text */}
        <p 
          className="text-center leading-snug text-[#1F120A] break-words px-4"
          style={{
            ...styleObject,
            wordBreak: 'normal',
            overflowWrap: 'anywhere',
            hyphens: 'none',
            fontSize: displayText.length > 120 ? '0.85rem' : displayText.length > 80 ? '1rem' : displayText.length > 50 ? '1.1rem' : '1.25rem',
            paddingBottom: '8px' // Extra space for text decorations like wavy underline
          }}
        >
          {displayText}
        </p>
      </div>

      {/* Share Buttons for Preview */}
      {showShareButtons && font && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="preview-share-buttons">
          <span className="text-xs text-[#C2B49A] mr-2 font-serif">Share Preview:</span>
          <button
            onClick={() => handleShare('twitter')}
            className="p-2 rounded-lg border border-[#D4AF37]/20 hover:bg-[#1DA1F2] hover:border-[#1DA1F2] hover:text-white text-[#C2B49A] transition-all"
            title="Share on Twitter"
            data-testid="preview-share-twitter"
          >
            <Twitter className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleShare('facebook')}
            className="p-2 rounded-lg border border-[#D4AF37]/20 hover:bg-[#4267B2] hover:border-[#4267B2] hover:text-white text-[#C2B49A] transition-all"
            title="Share on Facebook"
            data-testid="preview-share-facebook"
          >
            <Facebook className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleShare('linkedin')}
            className="p-2 rounded-lg border border-[#D4AF37]/20 hover:bg-[#0077B5] hover:border-[#0077B5] hover:text-white text-[#C2B49A] transition-all"
            title="Share on LinkedIn"
            data-testid="preview-share-linkedin"
          >
            <Linkedin className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleShare('pinterest')}
            className="p-2 rounded-lg border border-[#D4AF37]/20 hover:bg-[#E60023] hover:border-[#E60023] hover:text-white text-[#C2B49A] transition-all"
            title="Share on Pinterest"
            data-testid="preview-share-pinterest"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
            </svg>
          </button>
          <button
            onClick={() => handleShare('copy')}
            className="p-2 rounded-lg border border-[#D4AF37]/20 hover:bg-[#722F37] hover:border-[#722F37] hover:text-white text-[#C2B49A] transition-all"
            title="Copy Link"
            data-testid="preview-share-copy"
          >
            <Link2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export const FontPreviewCard = ({ font, onClick }) => {
  // Load Google Font dynamically when font changes
  useEffect(() => {
    if (font?.css_properties?.fontFamily) {
      const weight = font.style_properties?.weight || 400;
      loadGoogleFont(font.css_properties.fontFamily, weight);
    }
  }, [font?.css_properties?.fontFamily, font?.style_properties?.weight]);

  const styleObject = useMemo(() => {
    if (!font) return {};
    
    const style = {};
    
    if (font.css_properties) {
      Object.entries(font.css_properties).forEach(([key, value]) => {
        const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        style[camelKey] = value;
      });
    }
    
    if (font.style_properties) {
      const props = font.style_properties;
      if (props.weight) style.fontWeight = props.weight;
      if (props.style) style.fontStyle = props.style;
      if (props.letterSpacing) style.letterSpacing = props.letterSpacing;
      if (props.lineHeight) style.lineHeight = props.lineHeight;
      if (props.textTransform) style.textTransform = props.textTransform;
    }
    
    return style;
  }, [font]);

  return (
    <div 
      className="font-card p-6 cursor-pointer group"
      onClick={onClick}
      data-testid={`font-card-${font?.id}`}
    >
      <div className="mb-4">
        <span className="mono-label">{font?.name}</span>
      </div>
      
      <div className="min-h-[100px] flex items-center">
        <p 
          className="text-2xl text-[#F4EFE6]/90 group-hover:text-[#F4EFE6] transition-colors"
          style={styleObject}
        >
          {font?.preview_text || 'Abc'}
        </p>
      </div>
      
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-[#C2B49A] font-serif">{font?.description?.slice(0, 50)}...</span>
        <span className="price-tag">${font?.price?.toFixed(2)}</span>
      </div>
    </div>
  );
};
